import React, { useEffect, useRef, useState } from 'react';
import { Button, FormControl, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../../helpers/api';
import { ENDPOINTS } from '../../../helpers/constants';

const linkLooksValid = value => /^https?:\/\//i.test(value) || /^[\w.-]+\.[a-z]{2,}/i.test(value);

const SiteMapVirtualTourModal = ({ show, modelId, link, onHide, onSaved }) => {
  const existingLink = link && String(link).trim() ? String(link).trim() : '';
  const editing = Boolean(existingLink);
  const [draft, setDraft] = useState(existingLink);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  useEffect(() => {
    if (show) setDraft(existingLink);
  }, [show, modelId, existingLink]);

  const save = async () => {
    if (savingRef.current) return;

    const trimmed = draft.trim();
    if (trimmed && !linkLooksValid(trimmed)) {
      toast.error('Enter a valid virtual tour link');
      return;
    }
    if (!trimmed && !editing) {
      toast.error('Enter a valid virtual tour link');
      return;
    }
    if (!modelId) {
      toast.error('This unit has no model to attach a virtual tour to.');
      return;
    }

    savingRef.current = true;
    setSaving(true);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.MODELS_WITH_ID(modelId),
      requestBody: { virtualTourLink: trimmed || null },
      method: 'PUT'
    });
    savingRef.current = false;
    setSaving(false);

    if (error) {
      toast.error(error);
      return;
    }

    const saved = response && response.virtualTourLink ? String(response.virtualTourLink).trim() : trimmed || null;
    if (onSaved) onSaved(modelId, saved);
    toast.success(saved ? 'Virtual tour link saved' : 'Virtual tour link removed');
    onHide();
  };

  const unchanged = draft.trim() === existingLink;

  return (
    <Modal show={show} onHide={saving ? undefined : onHide} centered backdrop="static">
      <Modal.Header closeButton={!saving}>
        <Modal.Title>
          <h6 className="mb-0">{editing ? 'Edit Virtual Tour Link' : 'Add Virtual Tour Link'}</h6>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormControl
          size="sm"
          type="url"
          placeholder="Paste virtual tour link"
          value={draft}
          disabled={saving}
          aria-label="Virtual tour link"
          autoFocus
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (!unchanged) save();
            }
          }}
        />
        <p className="small text-muted mb-0 mt-2">
          Saved on this model, so every unit of the model uses this link.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" size="sm" variant="outline-secondary" disabled={saving} onClick={onHide}>
          Cancel
        </Button>
        <Button type="button" size="sm" variant="primary" disabled={saving || unchanged || (!editing && !draft.trim())} onClick={save}>
          {saving ? 'Saving…' : editing ? 'Save' : 'Add'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SiteMapVirtualTourModal;
